import {
  getPushPublicKey,
  subscribePush,
  unsubscribePush,
} from '../services/notification'

export const isPushSupported = (): boolean =>
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window

const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

// Not navigator.serviceWorker.ready — the SW only registers in production builds,
// so .ready never resolves in `npm start` and would hang the toggle forever.
const getRegistration = async (): Promise<
  ServiceWorkerRegistration | undefined
> => {
  if (!('serviceWorker' in navigator)) return undefined
  return navigator.serviceWorker.getRegistration()
}

// For the enable flow only: give a worker that is mid-install a few seconds to
// show up, without hanging forever in `npm start` where none is registered.
const waitForRegistration = async (
  timeoutMs = 4000
): Promise<ServiceWorkerRegistration | undefined> => {
  const existing = await getRegistration()
  if (existing) return existing
  if (!('serviceWorker' in navigator)) return undefined
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<undefined>((resolve) =>
      setTimeout(() => resolve(undefined), timeoutMs)
    ),
  ])
}

export const getPushSubscription =
  async (): Promise<PushSubscription | null> => {
    if (!isPushSupported()) return null
    const registration = await getRegistration()
    if (!registration) return null
    return registration.pushManager.getSubscription()
  }

/**
 * Full enable flow: permission (must run from a user gesture) → fetch the VAPID
 * public key from the backend → browser subscribe → register with the backend.
 */
export const enablePush = async (): Promise<void> => {
  if (!isPushSupported()) {
    throw new Error('Push notifications are not supported on this device')
  }
  const registration = await waitForRegistration()
  if (!registration) {
    throw new Error(
      'The app is still installing. Please try again in a moment.'
    )
  }

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    throw new Error('Notification permission was not granted')
  }

  const { data } = await getPushPublicKey()
  if (!data?.enabled || !data?.publicKey) {
    throw new Error('Push notifications are not configured on the server')
  }

  let subscription = await registration.pushManager.getSubscription()
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.publicKey),
    })
  }

  const { endpoint, keys } = subscription.toJSON()
  await subscribePush({
    body: { endpoint, keys, userAgent: navigator.userAgent },
  })
}

/**
 * Browser-side unsubscribe only (no API call) — safe to run when auth is already
 * gone (forced logout). Returns the endpoint that was unsubscribed, if any.
 */
export const disablePushLocal = async (): Promise<string | null> => {
  const subscription = await getPushSubscription()
  if (!subscription) return null
  const { endpoint } = subscription
  await subscription.unsubscribe()
  return endpoint
}

export const disablePush = async (): Promise<void> => {
  const endpoint = await disablePushLocal()
  if (!endpoint) return
  try {
    await unsubscribePush({ endpoint })
  } catch (error) {
    // Best-effort: the backend row self-heals via 410 on the next send anyway
  }
}

// Per-user, per-device memory of an explicit "turn push off" — the only thing
// that stops the seamless auto-restore on login
const OPT_OUT_KEY_PREFIX = 'oeta-push-off:'

export const hasPushOptOut = (userId: string): boolean => {
  try {
    return localStorage.getItem(OPT_OUT_KEY_PREFIX + userId) === '1'
  } catch (error) {
    return false
  }
}

export const setPushOptOut = (userId: string, off: boolean): void => {
  try {
    if (off) {
      localStorage.setItem(OPT_OUT_KEY_PREFIX + userId, '1')
    } else {
      localStorage.removeItem(OPT_OUT_KEY_PREFIX + userId)
    }
  } catch (error) {
    // Storage unavailable — auto-restore stays on, worst case is an extra toggle
  }
}

/**
 * Seamless login restore: if this browser already holds notification permission
 * and the logged-in user hasn't explicitly turned push off on this device,
 * ensure a subscription exists and belongs to them. Covers both the
 * shared-device ownership transfer and "push silently off after re-login".
 * Never prompts — permission requests stay behind the toggle's user gesture.
 */
export const ensurePushForUser = async (userId: string): Promise<void> => {
  if (!userId || !isPushSupported()) return
  if (Notification.permission !== 'granted') return
  if (hasPushOptOut(userId)) return
  const registration = await getRegistration()
  if (!registration) return

  let subscription = await registration.pushManager.getSubscription()
  if (!subscription) {
    const { data } = await getPushPublicKey()
    if (!data?.enabled || !data?.publicKey) return
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.publicKey),
    })
  }

  const { endpoint, keys } = subscription.toJSON()
  await subscribePush({
    body: { endpoint, keys, userAgent: navigator.userAgent },
  })
}
